import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocale } from '../../localization/LocaleContext';
import { useCurrency } from '../../localization/CurrencyContext';
import './AIEstimateDemo.css';

const TYPING_SPEED = 38;
const RESTART_DELAY = 1800;
const RECEIPT_LINE_WIDTH = 32;

function formatReceiptLine(label, value) {
    const baseLabel = `${label}`;
    const baseValue = `${value}`;
    const fillerWidth = Math.max(4, RECEIPT_LINE_WIDTH - baseLabel.length - baseValue.length);
    return `${baseLabel}${'.'.repeat(fillerWidth)}${baseValue}`;
}

export default function AIEstimateDemo() {
    const { t, toLocalizedPath } = useLocale();
    const { formatCurrency } = useCurrency();
    const [typedLength, setTypedLength] = useState(0);

    const laborItems = [
        [t('aiDemo.laborDemolition'), formatCurrency(t('aiDemo.laborDemolitionValue'))],
        [t('aiDemo.laborPlumbing'), formatCurrency(t('aiDemo.laborPlumbingValue'))],
        [t('aiDemo.laborTileLaying'), formatCurrency(t('aiDemo.laborTileLayingValue'))],
    ];

    const materialItems = [
        [t('aiDemo.materialCeramicTiles'), formatCurrency(t('aiDemo.materialCeramicTilesValue'))],
        [t('aiDemo.materialAdhesive'), formatCurrency(t('aiDemo.materialAdhesiveValue'))],
        [t('aiDemo.materialGrout'), formatCurrency(t('aiDemo.materialGroutValue'))],
        [t('aiDemo.materialToilet'), formatCurrency(t('aiDemo.materialToiletValue'))],
        [t('aiDemo.materialWashBasin'), formatCurrency(t('aiDemo.materialWashBasinValue'))],
    ];

    const receiptText = [
        t('aiDemo.receiptLabor'),
        ...laborItems.map(([label, value]) => formatReceiptLine(`  ${label}`, value)),
        '',
        t('aiDemo.receiptMaterials'),
        ...materialItems.map(([label, value]) => formatReceiptLine(`  ${label}`, value)),
        '------------------------------',
        formatReceiptLine(t('aiDemo.receiptTotal'), formatCurrency(t('aiDemo.receiptTotalValue'))),
    ].join('\n');

    useEffect(() => {
        if (typedLength < receiptText.length) {
            const timerId = window.setTimeout(() => {
                setTypedLength(typedLength + 1);
            }, TYPING_SPEED);

            return () => window.clearTimeout(timerId);
        }

        const restartId = window.setTimeout(() => {
            setTypedLength(0);
        }, RESTART_DELAY);

        return () => window.clearTimeout(restartId);
    }, [typedLength, receiptText]);

    return (
        <section className="ai-demo-section sp-90">
            <div className="container">
                <div className="ai-demo-header">
                    {/* <span className="ai-demo-badge">{t('aiDemo.badge')}</span> */}
                    <div className="vision-ai-badge">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                    {/* Large sparkle */}
                                    <path d="M8 1 C8.5 4.5 11.5 7.5 16 9 C11.5 10.5 8.5 13.5 8 17 C7.5 13.5 4.5 10.5 0 9 C4.5 7.5 7.5 4.5 8 1 Z"/>
                                    {/* Small sparkle */}
                                    <path d="M18 13 C18.25 14.5 19.75 16.5 22 17 C19.75 17.5 18.25 19.5 18 21 C17.75 19.5 16.25 17.5 14 17 C16.25 16.5 17.75 14.5 18 13 Z"/>
                                </svg>
                                {t('vision.aiBadge')}
                            </div>
                    <h2 className="h-1 h-bold ai-demo-heading">{t('aiDemo.heading')}</h2>
                    <p className="paragraph ai-demo-subheading">{t('aiDemo.subheading')}</p>
                </div>

                <div className="ai-demo-shell">
                    <div className="ai-demo-photos" aria-label={t('aiDemo.photosAriaLabel')}>
                        <div className="ai-demo-photo-card ai-demo-photo-card-large">
                            <img src="/images/bathroom-old-1.png" alt={t('aiDemo.photo1Alt')} className="ai-demo-photo" />
                        </div>
                        <div className="ai-demo-photo-stack">
                            <div className="ai-demo-photo-card">
                                <img src="/images/bathroom-old-2.png" alt={t('aiDemo.photo2Alt')} className="ai-demo-photo" />
                            </div>
                            <div className="ai-demo-photo-card">
                                <img src="/images/bathroom-old-3.png" alt={t('aiDemo.photo3Alt')} className="ai-demo-photo" />
                            </div>
                        </div>
                    </div>

                    <div className="ai-demo-receipt-panel">
                        <div className="ai-demo-receipt-glow" />
                        <div className="ai-demo-receipt-card">
                            <div className="ai-demo-receipt-topbar">
                                <span className="ai-demo-dot" />
                                <span className="ai-demo-dot" />
                                <span className="ai-demo-dot" />
                                <span className="ai-demo-topbar-label">{t('aiDemo.topbarLabel')}</span>
                            </div>

                            <div className="ai-demo-receipt-meta">
                                <span className="ai-demo-receipt-title">{t('aiDemo.receiptTitle')}</span>
                                <span className="ai-demo-receipt-status">{t('aiDemo.receiptStatus')}</span>
                            </div>

                            <pre className="ai-demo-receipt-body">
                                {receiptText.slice(0, typedLength)}
                                <span className="ai-demo-cursor" aria-hidden="true">|</span>
                            </pre>

                            <div className="ai-demo-receipt-footer">
                                <div className="ai-demo-chip">{t('aiDemo.footerChip')}</div>
                                <p>{t('aiDemo.footerNote')}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="demo-cta-block ai-demo-cta-block">
                    <Link to={toLocalizedPath('pricing')} className="demo-cta-btn">
                        {t('home.aiEstimatorCta')}
                    </Link>
                    <p className="demo-cta-subtext">{t('aiDemo.ctaSubtext')}</p>
                </div>
            </div>
        </section>
    );
}